import React from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { wrapCreateBrowserRouterV7 } from '@sentry/react';
import { HelmetProvider } from 'react-helmet-async';

const sentryCreateBrowserRouter = wrapCreateBrowserRouterV7(createBrowserRouter);
import { AuthProvider } from './hooks/useAuth';
import { SubscriptionProvider } from './hooks/useSubscription';
import { PlanProvider } from './hooks/usePlan';
import { UpgradeGateProvider } from './components/billing/UpgradeGate';
import { ToastProvider } from './components/ui/Toast';

// Auth
import { AuthLayout } from './components/auth/AuthLayout';
import { LoginPage } from './components/auth/LoginPage';
import { RegisterPage } from './components/auth/RegisterPage';
import { ForgotPasswordPage, ResetPasswordPage } from './components/auth/ResetPasswordPage';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { SubscriptionGate } from './components/auth/SubscriptionGate';

// Layout
import { DashboardLayout } from './components/DashboardLayout';

// Forms
import { FormsDashboard } from './components/forms/FormsDashboard';
import { SlideEditor } from './components/forms/editor/SlideEditor';
import { FormPublic } from './components/forms/FormPublic';
import { FormPublicBySlug } from './components/forms/FormPublicBySlug';
import { FormResponses } from './components/forms/FormResponses';
import { FormShare } from './components/forms/FormShare';
import { FormsAllList } from './components/forms/FormsAllList';
import { FormTemplates } from './components/forms/FormTemplates';
import { FormsResponsesAll } from './components/forms/FormsResponsesAll';

// Settings
import { SettingsGeneral } from './components/settings/SettingsGeneral';
import { ProfilePanel } from './components/settings/ProfilePanel';

// Landing
import { LandingPage } from './components/LandingPage';

// Pricing (public)
import { PricingPage } from './components/pricing/PricingPage';

// saas-1.19 spike (research-only, public route)
import { SpikePage as PdfV2SpikePage } from './lib/pdf/v2/spike/SpikePage';

// Onboarding (auth required)
import { OnboardingPlans } from './components/onboarding/OnboardingPlans';

// Admin
import { AdminLayout } from './components/admin/AdminLayout';
import { AdminDashboard } from './components/admin/AdminDashboard';
import { AdminUsers } from './components/admin/AdminUsers';
import { AdminRevenue } from './components/admin/AdminRevenue';
import { AdminBillingPanel } from './components/admin/AdminBillingPanel';
import { AdminLogs } from './components/admin/AdminLogs';
import { AdminMarketing } from './components/admin/AdminMarketing';
import { AdminMobileBuilder } from './components/admin/AdminMobileBuilder';
import { AdminTemplates } from './components/admin/AdminTemplates';
import { AdminEmail } from './components/admin/AdminEmail';
import { TrackingInjector } from './components/tracking/TrackingInjector';
import { SurfaceReset } from './components/SurfaceReset';

// Debug
import { DebugPanel } from './components/debug/DebugPanel';
import { ErrorBoundary } from './components/ErrorBoundary';

const router = sentryCreateBrowserRouter([
  // Landing page (public)
  {
    path: '/',
    element: <LandingPage />,
  },

  // Pricing page (public)
  {
    path: '/pricing',
    element: <PricingPage />,
  },

  // saas-1.19 spike (research-only, public)
  {
    path: '/spike/pdf-v2',
    element: <PdfV2SpikePage />,
  },

  // Public: form view via vanity URL (/@username/slug)
  // React Router v6 doesn't support partial params like /@:username,
  // so we capture the full segment and strip the @ prefix in the component
  {
    path: '/:userHandle/:slug',
    element: <FormPublicBySlug />,
  },
  {
    path: '/f/:formId',
    element: <FormPublic />,
  },

  // Legacy redirects
  { path: '/login', element: <Navigate to="/auth/login" replace /> },
  { path: '/register', element: <Navigate to="/auth/register" replace /> },
  { path: '/forgot-password', element: <Navigate to="/auth/forgot-password" replace /> },
  { path: '/reset-password', element: <Navigate to="/auth/reset-password" replace /> },

  // Auth routes
  {
    path: '/auth',
    element: <AuthLayout />,
    children: [
      { path: 'login', element: <LoginPage /> },
      { path: 'register', element: <RegisterPage /> },
      { path: 'forgot-password', element: <ForgotPasswordPage /> },
      { path: 'reset-password', element: <ResetPasswordPage /> },
    ],
  },

  // Protected routes
  {
    element: <ProtectedRoute />,
    children: [
      // Onboarding tier picker (pós-signup, full-screen, sem DashboardLayout)
      { path: '/onboarding/plans', element: <OnboardingPlans /> },
      {
        element: <SubscriptionGate />,
        children: [
          // Standalone editor routes (full-screen, no DashboardLayout)
          { path: '/dashboard/forms/new', element: <SlideEditor /> },
          { path: '/dashboard/forms/:id/edit', element: <SlideEditor /> },
          {
            element: <DashboardLayout />,
            children: [
              { path: '/dashboard', element: <FormsDashboard /> },
              { path: '/dashboard/forms', element: <FormsDashboard /> },
              { path: '/dashboard/forms/all', element: <FormsAllList /> },
              { path: '/dashboard/forms/templates', element: <FormTemplates /> },
              { path: '/dashboard/forms/responses', element: <FormsResponsesAll /> },
              { path: '/dashboard/forms/:id/responses', element: <FormResponses /> },
              { path: '/dashboard/forms/:id/share', element: <FormShare /> },
              { path: '/dashboard/settings', element: <SettingsGeneral /> },
              { path: '/dashboard/profile', element: <ProfilePanel /> },
            ],
          },
        ],
      },
    ],
  },

  // Admin routes (superadmin only)
  {
    element: <ProtectedRoute requireSuperAdmin />,
    children: [
      {
        path: '/admin',
        element: <AdminLayout />,
        children: [
          { index: true, element: <Navigate to="/admin/overview" replace /> },
          { path: 'overview', element: <AdminDashboard /> },
          { path: 'users', element: <AdminUsers /> },
          { path: 'revenue', element: <AdminRevenue /> },
          { path: 'plans', element: <AdminBillingPanel /> },
          { path: 'logs', element: <AdminLogs /> },
          { path: 'marketing', element: <AdminMarketing /> },
          { path: 'templates', element: <AdminTemplates /> },
          { path: 'email', element: <AdminEmail /> },
          { path: 'mobile-builder', element: <AdminMobileBuilder /> },
          { path: '*', element: <Navigate to="/admin/overview" replace /> },
        ],
      },
    ],
  },
]);

const App: React.FC = () => {
  return (
    <HelmetProvider>
      <AuthProvider>
        <SubscriptionProvider>
          <PlanProvider>
            <UpgradeGateProvider>
              <ToastProvider>
                <ErrorBoundary>
                  <SurfaceReset />
                  <TrackingInjector />
                  <RouterProvider router={router} />
                  <DebugPanel />
                </ErrorBoundary>
              </ToastProvider>
            </UpgradeGateProvider>
          </PlanProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </HelmetProvider>
  );
};

export default App;
